import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Tooltip } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SmartValueInput from '@/components/CustomInput/SmartValueInput'

// --- Styled Components ---

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 0px;
`

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    width: 100%;
`

const LabelWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const LabelText = styled.span`
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: 0.1px;
    display: flex;
    align-items: center;
`

const HelpIconWrapper = styled.div`
    display: flex;
    align-items: center;
    color: rgba(255, 255, 255, 0.4);
    cursor: help;
    transition: color 0.2s;

    svg {
        font-size: 14px;
    }

    &:hover {
        color: #6366f1;
    }
`

const PresetsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
    justify-content: flex-end;
    align-items: flex-end;
    width: 100%;
`

const PresetButton = styled.button`
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.03)'};
    border: 1px solid
        ${({ active }) => (active ? '#6366f1' : 'rgba(255, 255, 255, 0.1)')};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.5)')};
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.65rem;
    font-family: var(--font-roboto), monospace;
    font-weight: 400;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: ${({ active }) =>
        active ? '#6366f1' : 'rgba(255, 255, 255, 0.3)'};
        color: white;
    }
`

// We don't want the default Slider's header since we built our own
const StyledSliderInput = styled.input`
    -webkit-appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.1);
    outline: none;
    transition: background 0.2s;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    margin: 8px 0;
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #6366f1;
        cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
        transition:
            transform 0.1s,
            background-color 0.2s;
        box-shadow: 0 0 0 2px #050208;
        margin-top: -5px;

        &:hover {
            transform: scale(1.1);
            filter: brightness(1.2);
        }
    }

    &::-moz-range-thumb {
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #6366f1;
        cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
        border: 2px solid #050208;
        transition: transform 0.1s;

        &:hover {
            transform: scale(1.1);
            filter: brightness(1.2);
        }
    }
`

// ... (Styles anteriores)

const HeaderRightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`

const HeaderPresetsContainer = styled.div`
    display: flex;
    gap: 4px;
`

const HeaderPresetButton = styled.button`
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
    border: 1px solid
        ${({ active }) => (active ? '#6366f1' : 'transparent')};
    color: ${({ active }) => (active ? 'white' : 'rgba(255, 255, 255, 0.5)')};
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.6rem;
    font-family: var(--font-roboto), monospace;
    font-weight: 400;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s;
    min-width: 24px;
    text-align: center;
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }
`

export default function ConfigSlider({
    label,
    description, // Helper tooltip text
    value,
    min,
    max,
    step,
    onChange,
    presets = [], // Array of values like [1, 2, 3] or objects { label: 'Small', val: 1 }
    unit = '', // Optional suffix for the value
    showPresetsInline = false, // New prop: show presets directly below slider (deprecated in favor of placement?)
    showPresetsInPopover = true, // New prop: show presets inside the popup
    showPresetsInHeader = true, // New prop: show presets in the header
    showSliderInline = true, // Default: show slider inline
    showSliderInPopover = true, // Default: show slider in popup
    presetsPlacement = 'bottom', // 'bottom' | 'header'
    disabled = false
}) {
    const [isHighlighted, setIsHighlighted] = useState(false)
    const highlightTimer = useRef(null)

    const triggerHighlight = () => {
        setIsHighlighted(true)
        if (highlightTimer.current) clearTimeout(highlightTimer.current)
        highlightTimer.current = setTimeout(() => {
            setIsHighlighted(false)
        }, 1000) // Keep highlighted for 1 second
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (highlightTimer.current) clearTimeout(highlightTimer.current)
        }
    }, [])

    // Helper to determine if a preset is active
    const isPresetActive = (pVal) => {
        return value === pVal
    }

    const handlePresetClick = (pValue) => {
        if (!disabled) {
            onChange(pValue)
            triggerHighlight()
        }
    }

    const handleSliderChange = (e) => {
        if (!disabled) {
            onChange(parseFloat(e.target.value))
            triggerHighlight()
        }
    }

    const renderPresets = (Wrapper, Button) => (
        <Wrapper>
            {presets.map((preset, index) => {
                const pValue = typeof preset === 'object' ? preset.val : preset
                const pLabel = typeof preset === 'object' ? preset.label : preset

                return (
                    <Button
                        key={index}
                        active={isPresetActive(pValue)}
                        onClick={() => handlePresetClick(pValue)}
                        disabled={disabled}
                    >
                        {pLabel}
                    </Button>
                )
            })}
        </Wrapper>
    )

    return (
        <Container style={{ opacity: disabled ? 0.4 : 1 }}>
            {/* Header: Label + Help (?) + Value Badge (Smart Input) */}
            <HeaderRow>
                <LabelWrapper>
                    <LabelText>{label}</LabelText>
                    {description && (
                        <Tooltip title={description} arrow placement="right">
                            <HelpIconWrapper>
                                <HelpOutlineIcon />
                            </HelpIconWrapper>
                        </Tooltip>
                    )}
                </LabelWrapper>

                <HeaderRightSection>
                    {/* Header Presets */}
                    {showPresetsInline && presetsPlacement === 'header' && presets.length > 0 &&
                        renderPresets(HeaderPresetsContainer, HeaderPresetButton)
                    }

                    <SmartValueInput
                        value={value}
                        onChange={onChange}
                        min={min}
                        max={max}
                        step={step}
                        presets={showPresetsInPopover ? presets : []}
                        unit={unit}
                        showSlider={showSliderInPopover}
                        label={label}
                        disabled={disabled}
                        isHighlighted={isHighlighted}
                    />
                </HeaderRightSection>
            </HeaderRow>

            {/* Slider Input (Conditionally Rendered) */}
            {showSliderInline && (
                <StyledSliderInput
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={handleSliderChange}
                    disabled={disabled}
                />
            )}

            {/* Inline Presets Row (Bottom) */}
            {showPresetsInline && presetsPlacement === 'bottom' && presets.length > 0 &&
                renderPresets(PresetsContainer, PresetButton)
            }
        </Container>
    )
}
